import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router"
import { useQueryOrganizationForUser } from "../../hooks/useQueryOrganizationForUser"
import { useClient } from "../../providers/supabase"
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit"
import { OnboardingFlowState, setSubscription } from "../../reduxToolkit/membershipSlice"

// "Next step: …" label for the entry link, keyed by which step the user starts
// on. Exhaustive over OnboardingFlowState so a reordering or new first step
// renders correct text instead of silently falling back to a default.
const FIRST_STEP_LABELS: Record<OnboardingFlowState, string> = {
  setMembership: 'Enter your Membership Number',
  fillProfile: 'Complete your profile',
  consent: 'Set your contact preferences',
  enableTwoFactor: 'Enable two-factor authentication',
  startTour: 'Take the orientation tour',
}

export function OnboardingHome({
  hasCompletedOnboarding,
  firstStep = 'setMembership',
}: {
  hasCompletedOnboarding?: boolean | null | undefined
  // The first onboarding step to send the user to. Defaults to 'setMembership',
  // but an invited spouse skips that step and starts at 'fillProfile'.
  firstStep?: OnboardingFlowState
}) {
  const [organization, { loading: organizationLoading, error: organizationError }] = useQueryOrganizationForUser()
  const client = useClient()
  const dispatch = useAppDispatch()
  const membershipId = useAppSelector(state => state.membership.membershipId)

  const [subscriptionLoading, setSubscriptionLoading] = useState(true)

  const [tosChecked, setTosChecked] = useState(false)
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false)
  const canContinue = tosChecked && privacyPolicyChecked

  useEffect(() => {
    // Query for existing subscription
    async function loadSubscription() {
      if (!membershipId) {
        setSubscriptionLoading(false)
        return
      }

      try {
        const { data, error } = await client
          .from('subscriptions')
          .select('*')
          .eq('membership_id', membershipId)
          .maybeSingle()

        if (!error && data) {
          // Update Redux store
          dispatch(setSubscription({ subscription: data }))
        }
      } catch (error) {
        console.error('Error loading subscription:', error)
      } finally {
        setSubscriptionLoading(false)
      }
    }

    loadSubscription()
  }, [membershipId, client, dispatch])

  if (organizationError) { throw organizationError }

  if (hasCompletedOnboarding) {
    return <div className="row">
      <div className="col-12 col-xl-8 offset-xl-2">
        <h1>Thanks!</h1>
        <p>You are now ready to use the tracker.</p>
        <p><Link to="/">Start tracking your expenses</Link></p>
      </div>
    </div>
  }

  if (organizationLoading || subscriptionLoading) {
    return <div className="row">
      <div className="col-12 col-xl-8 offset-xl-2">
        <div className={`loading-bar`}></div>
      </div>
    </div>
  }

  return <div className="row mt-4">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Welcome to the HealthShare Reimbursement Tracker!</h1>
      <p>
        Here you can:
        <ul>
          <li>Keep track of your medical expenses</li>
          <li>Get clarity on whether to submit to CHM or your HRA</li>
          <li>Get your forms filled out for you</li>
          <li>Be reminded of important dates and deadlines</li>
        </ul>
      </p>

      <h6>Let's get started!</h6>
      <p>
        <form>
          <div className="form-check" onClick={() => {
              setTosChecked(!tosChecked)
            }}>
            <label className="form-check-label">
              Read and agree to the&nbsp;
              <a href="https://www.healthsharetech.com/terms-of-service/" target='_blank' rel="noreferrer">Terms and Conditions</a>
            </label>
            <input className="form-check-input" type="checkbox"
              checked={tosChecked} />
          </div>

          <div className="form-check" onClick={() => {
              setPrivacyPolicyChecked(!privacyPolicyChecked)
            }}>
            <label className="form-check-label">
              Read the&nbsp;
              <a href="https://www.healthsharetech.com/privacy-policy/" target='_blank' rel="noreferrer">Privacy Policy</a>
            </label>
            <input className="form-check-input" type="checkbox"
              checked={privacyPolicyChecked} />
          </div>
        </form>
      </p>
      <p>
        <Link to={firstStep} className={`btn btn-link ${!canContinue && 'disabled'}`} aria-disabled>
          {`Next step: ${FIRST_STEP_LABELS[firstStep]}`}
        </Link>
      </p>
    </div>
  </div>
}

export function OnboardingSubscribeSuccess({
  firstStep = 'setMembership',
}: {
  // First onboarding step to continue to. Defaults to 'setMembership'; an
  // invited spouse skips that and continues at 'fillProfile' (mirrors
  // OnboardingHome). Without this they'd hit the skipped-step redirect guard.
  firstStep?: OnboardingFlowState
} = {}) {
  const navigate = useNavigate()
  const client = useClient()
  const dispatch = useAppDispatch()
  const membershipId = useAppSelector(state => state.membership.membershipId)
  const [isPolling, setIsPolling] = useState(true)
  const [subscription, setSubscriptionState] = useState<any>(null)

  useEffect(() => {
    if (!membershipId) return

    let isCancelled = false
    let pollAttempts = 0
    const maxAttempts = 30 // 30 seconds max

    async function pollForSubscription() {
      if (isCancelled || pollAttempts >= maxAttempts) {
        setIsPolling(false)
        return
      }

      try {
        // Query the subscriptions table directly
        const { data, error } = await client
          .from('subscriptions')
          .select('*')
          .eq('membership_id', membershipId)
          .maybeSingle()

        if (!error && data) {
          setSubscriptionState(data)
          // Update Redux store
          dispatch(setSubscription({ subscription: data }))
          setIsPolling(false)
          return
        }
      } catch (error) {
        console.error('Error polling for subscription:', error)
      }

      pollAttempts++
      setTimeout(pollForSubscription, 1000) // Poll every second
    }

    pollForSubscription()

    return () => {
      isCancelled = true
    }
  }, [membershipId, client, dispatch])

  return <div className="row">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Subscription Active!</h1>
      {isPolling ? (
        <>
          <p>
            <div className="loading-bar"></div>
          </p>
          <p className="text-muted">
            Processing your subscription...
          </p>
        </>
      ) : subscription ? (
        <>
          <p>
            Your subscription has been set up successfully.
            Let's continue setting up your account.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/${firstStep}`)}
          >
            Continue Setup
          </button>
        </>
      ) : (
        <>
          <p className="text-warning">
            We're still processing your subscription. This may take a moment.
          </p>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(`/${firstStep}`)}
          >
            Continue Anyway
          </button>
        </>
      )}
    </div>
  </div>
}
